import React, { useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  ShoppingCart, 
  Menu, 
  X,
  Search,
  Cylinder,
  Wrench,
  User,
  LogOut,
  Package,
  Settings,
  ChevronDown,
  Phone,
  Clock,
  MapPin
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { AuthModal } from './AuthModal';
import { useClickOutside } from '../hooks/useClickOutside';

const Navbar: React.FC = () => {
  const { state } = useCart();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  useClickOutside(mobileMenuRef, () => setIsMobileMenuOpen(false));
  useClickOutside(searchRef, () => setIsSearchOpen(false));
  useClickOutside(profileRef, () => setIsProfileMenuOpen(false));

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    window.location.href = `/?search=${searchTerm}`;
    setIsSearchOpen(false);
    setSearchTerm('');
  };

  const handleLogout = () => {
    logout();
    setIsProfileMenuOpen(false);
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  const getInitials = () => {
    if (user) {
      const firstName = user.user_first_name || '';
      return `${firstName.charAt(0)}`.toUpperCase();
    }
    return '';
  };

  const navigation = [
    { name: 'HOME', path: '/' },
    { name: 'GAS CYLINDERS', path: '/gas-cylinders', icon: Cylinder },
    { name: 'ACCESSORIES', path: '/accessories', icon: Wrench },
    { name: 'ABOUT', path: '/about' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-lg dark:bg-gray-800">
      <nav className="max-w-7xl mx-auto px-10 py-2">
        <div className="flex justify-between h-16">
          {/* Logo and Brand */}
          <div className="flex justify-items-center">
            <Link to="/">
              <img
                src="/logo/png/logo1.png"
                alt="Eveready gas"
                className="h-14 w-auto"
              />
              {/* <div className="hidden md:block"> */}
              {/* <span className="text-xl font-bold text-green-500">Eveready</span> */}
              {/* <span className="text-xl font-bold text-green-500 block">Gas</span> */}
              {/* </div> */}
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`text-xl font-medium transition-colors ${
                  location.pathname === item.path
                    ? "text-primary-500"
                    : "text-gray-700 hover:text-primary-500 dark:text-gray-300 dark:hover:text-primary-400"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Right Side Icons */}
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div ref={searchRef} className="relative">
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="p-2 text-gray-600 hover:text-primary-500 dark:text-gray-300 dark:hover:text-primary-400"
              >
                <Search className="h-6 w-6" />
              </button>

              {isSearchOpen && (
                <div className="absolute right-0 mt-2 w-screen md:w-72 bg-white dark:bg-gray-800 rounded-md shadow-lg p-4 ring-1 ring-black ring-opacity-5">
                  <form onSubmit={handleSearch}>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search products..."
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                      autoFocus
                    />
                  </form>
                </div>
              )}
            </div>

            {/* Cart */}
            <Link to="/cart" className="relative p-2">
              <ShoppingCart className="h-6 w-6 text-gray-600 hover:text-primary-500 dark:text-gray-300 dark:hover:text-primary-400" />
              {state.items.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                  {state.items.length}
                </span>
              )}
            </Link>

            {/* User Menu */}
            <div ref={profileRef} className="relative">
              <button
                onClick={() =>
                  user
                    ? setIsProfileMenuOpen(!isProfileMenuOpen)
                    : setShowAuthModal(true)
                }
                className="flex items-center space-x-2 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                  {user ? (
                    getInitials()
                  ) : (
                    <User className="h-5 w-5 text-primary-500 dark:text-primary-400" />
                  )}
                </div>
                <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400 hidden md:block" />
              </button>

              {user && isProfileMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5">
                  <div className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700">
                    {user.user_first_name}
                  </div>
                  <Link
                    to="/orders"
                    onClick={() => setIsProfileMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Package className="h-4 w-4 inline-block mr-2" />
                    My Orders
                  </Link>
                  <Link
                    to="/settings"
                    onClick={() => setIsProfileMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Settings className="h-4 w-4 inline-block mr-2" />
                    Settings
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <LogOut className="h-4 w-4 inline-block mr-2" />
                    Logout
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div
          ref={mobileMenuRef}
          className={`md:hidden fixed inset-0 top-16 bg-white dark:bg-gray-800 transform transition-transform duration-300 ease-in-out ${
            isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* User Profile Section */}
          {user ? (
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                  <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                    {getInitials()}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {user.user_first_name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {user.user_email}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowAuthModal(true);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full py-3 text-center text-white bg-primary-500 rounded-md hover:bg-primary-600"
              >
                Sign In
              </button>
            </div>
          )}

          {/* Navigation Links */}
          <div className="px-2 py-3 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-3 py-4 rounded-md text-base font-medium transition-colors ${
                  location.pathname === item.path
                    ? "text-primary-500 bg-primary-50 dark:bg-primary-900/20"
                    : "text-gray-700 hover:text-primary-500 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                }`}
              >
                <div className="flex items-center space-x-3">
                  {item.icon && <item.icon className="h-5 w-5" />}
                  <span>{item.name}</span>
                </div>
              </Link>
            ))}
          </div>

          {/* Quick Links */}
          <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="space-y-4">
              <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-300">
                <Phone className="h-5 w-5" />
                <span>0798765432</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-300">
                <Clock className="h-5 w-5" />
                <span>8am - 9pm</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-300">
                <MapPin className="h-5 w-5" />
                <span>Delivery in 30 mins</span>
              </div>
            </div>
          </div>

          {/* User Actions */}
          {user && (
            <div className="px-2 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleLogout}
                className="w-full px-3 py-3 text-center text-red-600 bg-red-50 rounded-md hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40"
              >
                <div className="flex items-center justify-center space-x-2">
                  <LogOut className="h-5 w-5" />
                  <span>Logout</span>
                </div>
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => setShowAuthModal(false)}
        />
      )}
    </header>
  );
};

export default Navbar;